import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Stethoscope, Shield } from 'lucide-react';
import constant from '../../constant';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const mobileRegex = /^[0-9]{10}$/;

const countryCodes = [
  { code: '+1', label: '+1' },
  { code: '+44', label: '+44' },
  { code: '+91', label: '+91' },
  { code: '+61', label: '+61' },
  // Add more country codes as needed
];

const DoctorForm = ({ initialValues, hospitals, onSubmit, onCancel, isEditMode = false }) => {
  const [formData, setFormData] = useState({
    doctor_id: initialValues.doctor_id || '',
    name: initialValues.name || '',
    email: initialValues.email || '',
    mobile: initialValues.mobile || '',
    countryCode: initialValues.mobile ? initialValues.mobile.slice(0, initialValues.mobile.match(/\d{10}$/)?.index || 0) : '+1',
    mobileNumber: initialValues.mobile ? initialValues.mobile.match(/\d{10}$/)?.[0] || '' : '',
    is_active: initialValues.is_active === undefined ? true : initialValues.is_active,
  });
  const [errors, setErrors] = useState({});
  const [hospitalList, setHospitalList] = useState(hospitals || []);
  const [hospitalError, setHospitalError] = useState(null);

  useEffect(() => {
    setFormData({
      doctor_id: initialValues.doctor_id || '',
      name: initialValues.name || '',
      email: initialValues.email || '',
      mobile: initialValues.mobile || '',
      countryCode: initialValues.mobile ? initialValues.mobile.slice(0, initialValues.mobile.match(/\d{10}$/)?.index || 0) : '+1',
      mobileNumber: initialValues.mobile ? initialValues.mobile.match(/\d{10}$/)?.[0] || '' : '',
      is_active: initialValues.is_active === undefined ? true : initialValues.is_active,
    });
  }, [initialValues]);

  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setHospitalError('No authentication token found');
          return;
        }

        const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();
        if (response.ok) {
          setHospitalList(result);
        } else {
          setHospitalError(result.message || 'Failed to fetch hospitals');
        }
      } catch (error) {
        setHospitalError('Error fetching hospitals');
      }
    };

    if (!hospitals || hospitals.length === 0) {
      fetchHospitals();
    }
  }, [hospitals]);

  const validateForm = (data) => {
    const newErrors = {};

    if (!data.name || data.name.trim() === '') {
      newErrors.name = 'Name is required.';
    }
    if (!data.email || data.email.trim() === '') {
      newErrors.email = 'Email is required.';
    } else if (!emailRegex.test(data.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!data.mobileNumber || data.mobileNumber.trim() === '') {
      newErrors.mobileNumber = 'Mobile number is required.';
    } else if (!mobileRegex.test(data.mobileNumber)) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number.';
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'countryCode' || name === 'mobileNumber') {
      setFormData((prev) => ({
        ...prev,
        mobile: prev.countryCode + prev.mobileNumber,
      }));
    }

    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const payload = {
        name: formData.name,
        email: formData.email,
        mobile: formData.countryCode + formData.mobileNumber,
        is_active: formData.is_active,
      };
      onSubmit(payload);
    }
  };

  const getInputClasses = (fieldName) => {
    const baseClasses = "w-full px-4 py-2 border rounded-r-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
    const errorClasses = "border-red-500 focus:ring-red-500 focus:border-red-500";
    const normalClasses = "border-gray-300";
    return `${baseClasses} ${errors[fieldName] ? errorClasses : normalClasses}`;
  };

  const getIconContainerClasses = (fieldName) => {
    const baseClasses = "inline-flex items-center px-3 rounded-l-md border border-r-0 bg-gray-50 text-gray-500";
    const errorClasses = "border-red-500";
    const normalClasses = "border-gray-300";
    return `${baseClasses} ${errors[fieldName] ? errorClasses : normalClasses}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="space-y-6 bg-white p-6 rounded-lg">
        {hospitalError && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
            {hospitalError}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name {errors.name && <span className="text-red-600">*</span>}
              </label>
              <div className="flex">
                <span className={getIconContainerClasses('name')}>
                  <User className="h-4 w-4" />
                </span>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={getInputClasses('name')}
                  aria-invalid={errors.name ? "true" : "false"}
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
              </div>
              {errors.name && <p id="name-error" className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email {errors.email && <span className="text-red-600">*</span>}
              </label>
              <div className="flex">
                <span className={getIconContainerClasses('email')}>
                  <Mail className="h-4 w-4" />
                </span>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={getInputClasses('email')}
                  aria-invalid={errors.email ? "true" : "false"}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
              </div>
              {errors.email && <p id="email-error" className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>

            {/* Mobile */}
            <div>
              <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Mobile Number {errors.mobileNumber && <span className="text-red-600">*</span>}
              </label>
              <div className="flex">
                <span className={getIconContainerClasses('mobileNumber')}>
                  <Phone className="h-4 w-4" />
                </span>
                <select
                  name="countryCode"
                  id="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  className="px-3 py-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {countryCodes.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  name="mobileNumber"
                  id="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  required
                  className={getInputClasses('mobileNumber')}
                  aria-invalid={errors.mobileNumber ? "true" : "false"}
                  aria-describedby={errors.mobileNumber ? "mobileNumber-error" : undefined}
                />
              </div>
              {errors.mobileNumber && <p id="mobileNumber-error" className="mt-1 text-xs text-red-600">{errors.mobileNumber}</p>}
            </div>

            {isEditMode && (
              <div>
                <label htmlFor="is_active" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="is_active"
                  id="is_active"
                  value={formData.is_active ? 'Active' : 'Inactive'}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'Active' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 mt-8">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {isEditMode ? 'Update Doctor' : 'Add Doctor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

DoctorForm.defaultProps = {
  initialValues: {
    doctor_id: '',
    name: '',
    email: '',
    mobile: '',
    is_active: true,
  },
  hospitals: [],
};

export default DoctorForm;